import { GeographicalSummaryData, MapOfBrands } from "~src/types/brandMapping";
import { BrandMappingFilters } from "./useBrandMappingV3";
import { BrandMappingContextV3 } from "~src/contexts/BrandMappingContextV3";
import { useContext } from "react";
import { COUNTRIES, CountryType } from "~src/data/countries";
import { mappingCountries } from "~src/data/mappingCounties";

/**
 * Filters the provided brands based on the criteria specified in filtersV3.
 */
export const filterMapOfBrands = (
  data: MapOfBrands[],
  filtersV3: BrandMappingFilters | undefined,
) => {
  let isProductCategoryMatch: boolean | undefined = true;
  let isBrandCountryMatch: boolean | undefined = true;
  let isAdCountryMatch: boolean | undefined = true;
  let isPriceInRange: boolean | undefined = true;
  let isTrafficInRange: boolean | undefined = true;
  let isRevenueInRange: boolean | undefined = true;
  let isSocialFollowersInRange: boolean | undefined = true;
  let hasRunningAds: boolean | undefined = true;

  return data.filter((brandObj) => {
    // ------------------------ Product category --------------------------
    const selectedCategories = filtersV3?.selectedCategories ?? [];
    if (selectedCategories.length) {
      isProductCategoryMatch = brandObj.products.some((product) =>
        selectedCategories.includes(product.category_id)
      );
    }
    // --------------------------------------------------------------------

    // ---------------------- Brand country filter -----------------------
    if (filtersV3?.brand_country) {
      isBrandCountryMatch = filtersV3.brand_country.includes(
        brandObj.brand.country
      );
    }
    // --------------------------------------------------------------------

    // -------------------- Ad filter (has_running_ads) --------------------
    if (filtersV3?.has_running_ads === true) {
      hasRunningAds = brandObj.ads.length > 0;
    }
    // --------------------------------------------------------------------

    // ------------------------ Ad country filter --------------------------
    const adCountryFilter = filtersV3?.ad_country ?? [];
    if (adCountryFilter.length > 0) {
      isAdCountryMatch = brandObj.ads.some((ad) =>
        ad &&
        ad.ad_country &&
        ad.ad_country.length > 0 &&
        ad.ad_country.some((country) => adCountryFilter.includes(country))
      );
    }

    // --------------------------------------------------------------------

    // ---------------------- Product Price Range filter ------------------
    if (filtersV3?.product_price_range) {
      const [min, max] = filtersV3.product_price_range.split("__").map(Number);
      isPriceInRange = brandObj.products.some(
        (product) => product.min_price >= min && product.max_price <= max
      );
    }
    // --------------------------------------------------------------------

    // -------------------- Estimated monthly revenue filter --------------
    if (filtersV3?.estimated_monthly_revenue) {
      const [min, max] = filtersV3.estimated_monthly_revenue
        .split("__")
        .map(Number);
      const revenue = brandObj.brand.store_data.estimated_monthly_revenue;
      isRevenueInRange = revenue >= min && revenue <= max;
    }
    // --------------------------------------------------------------------

    // -------------------- Estimated monthly traffic filter --------------
    if (filtersV3?.estimated_monthly_traffic) {
      const [min, max] = filtersV3.estimated_monthly_traffic
        .split("__")
        .map(Number);
      const traffic = brandObj.brand.store_data.estimated_monthly_traffic;
      isTrafficInRange = traffic >= min && traffic <= max;
    }
    // --------------------------------------------------------------------

    // -------------------- Social followers count filter -----------------
    if (filtersV3?.social_followers) {
      const [min, max] = filtersV3.social_followers.split("__").map(Number);
      const totalFollowers =
        brandObj.brand.latest_fblikes + brandObj.brand.latest_instafollowers;
      isSocialFollowersInRange =
        totalFollowers >= min && totalFollowers <= max;
    }
    // --------------------------------------------------------------------

    // Return true if all conditions are satisfied
    return (
      isProductCategoryMatch &&
      isBrandCountryMatch &&
      hasRunningAds &&
      isAdCountryMatch &&
      isPriceInRange &&
      isTrafficInRange &&
      isRevenueInRange &&
      isSocialFollowersInRange
    );
  });
};

/**
 * A custom hook that extracts a unique list of brand countries from context.
 */
export const useExtractCountries = () => {
  const { mapOfBrandsFullResponseContext } =
  useContext(BrandMappingContextV3) || {};

  // Get unique brand countries (filtering out any null or undefined values)
  const uniqueBrandCountries = Array.from(
    new Set(mapOfBrandsFullResponseContext?.map((item) => item.brand.country))
  ).filter((country) => country !== null && country !== undefined);

  const formatCountryList = (countries: string[]): CountryType[] => {
    return countries.map((countryCode) => {
      const countryMatch = COUNTRIES.find((c) => c.code === countryCode);
      return {
        code: countryCode,
        label: countryMatch ? countryMatch.label : countryCode.toUpperCase(),
      };
    });
  };

  return {
    brandCountries: formatCountryList(uniqueBrandCountries),
  };
};

/**
 * Filters brands based on whether their country is present in the predefined mapping.
 */
const filterBrandsByCountries = (brands: MapOfBrands[]) => {
  return brands.filter((brand) =>
    Object.prototype.hasOwnProperty.call(
      mappingCountries,
      brand.brand.country
    )
  );
};

type CountryCount = [string, number];

/**
 * Aggregates country-related data (ads, brands, shipping, traffic) from a list of brands.
 */
export const aggregateCountries = (
  data: MapOfBrands[]
): GeographicalSummaryData | undefined => {
  // Filter the countries with the predefined countries
  data = filterBrandsByCountries(data);

  // Helper function to count occurrences in a map
  const countOccurrences = (map: Map<string, number>, country: string) => {
    map.set(country, (map.get(country) || 0) + 1);
  };

  // Helper function to add traffic data to a map
  const addTraffic = (
    map: Map<string, number>,
    country: string,
    traffic: number
  ) => {
    map.set(country, (map.get(country) || 0) + traffic);
  };

  // Initialize maps to hold the country counts and traffic data
  const adCountryMap = new Map<string, number>();
  const brandCountryMap = new Map<string, number>();
  const shippingCountryMap = new Map<string, number>();
  const trafficCountryMap = new Map<string, number>();

  // Loop through the data array and populate the maps
  data.forEach((item) => {
    // Count the brand's primary country and add traffic data if available
    if (item.brand?.country) {
      countOccurrences(brandCountryMap, item.brand.country);
      const lastMonthTraffic = item.brand.web_traffic_growth?.last_month_traffic;
      if (lastMonthTraffic) {
        addTraffic(trafficCountryMap, item.brand.country, lastMonthTraffic);
      }
    }

    // Count each shipping country
    item?.brand?.shipping_to_countries?.forEach((country) =>
      countOccurrences(shippingCountryMap, country)
    );

    // Count each ad's countries
    item?.ads?.forEach((ad) =>
      ad?.ad_country?.forEach((country) =>
        countOccurrences(adCountryMap, country)
      )
    );
  });

  // Helper to convert a Map to a sorted array of [country, count] tuples
  const mapToSortedArray = (map: Map<string, number>): CountryCount[] =>
    Array.from(map)
      .sort((a, b) => b[1] - a[1])
      .map(([country, count]) => [country, count]);

  return {
    top_ad_countries: mapToSortedArray(adCountryMap).slice(0, 30),
    top_brand_countries: mapToSortedArray(brandCountryMap).slice(0, 30),
    top_shipping_countries: mapToSortedArray(shippingCountryMap).slice(0, 30),
    top_traffic_countries: mapToSortedArray(trafficCountryMap).slice(0, 30),
  };
};
